import is from '@sindresorhus/is';
import type { QueryKey, UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useApplicationContext } from '~/shared/context/use-application-context';
import type { JobLevel } from '~/shared/data-contract/job-level';
import type { JobLevelDetails } from '~/shared/data-contract/job-level-details';
import type { JobProfileVersions } from '~/shared/data-contract/job-profile-versions';
import type { JobRole } from '~/shared/data-contract/job-role';
import type { JobRoleDetails } from '~/shared/data-contract/job-role-details';

import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { jobArchitectureKeys } from './query-keys';

export type UsePublishJobItemVersionResult = UseMutationResult<
    unknown,
    Error,
    {
        readonly architectureId: string;
        readonly itemId: string;
        readonly itemType: 'job-level' | 'job-role';
        readonly version: string;
    }
>;

export const usePublishJobItemVersion = (): UsePublishJobItemVersionResult => {
    const { publishJobLevelVersion, publishJobRoleVersion } = useApplicationFetchers();
    const queryClient = useQueryClient();
    const { applicationState } = useApplicationContext();
    const { myUserProfile } = applicationState;

    return useMutation({
        mutationFn: async ({ architectureId, itemId, itemType, version }) => {
            const result =
                itemType === 'job-level'
                    ? await publishJobLevelVersion({
                          jobArchitectureId: architectureId,
                          jobLevelId: itemId,
                          version,
                      })
                    : await publishJobRoleVersion({
                          jobArchitectureId: architectureId,
                          jobRoleId: itemId,
                          version,
                      });

            if (result.isErr === true) {
                throw result.error;
            }

            return result.value;
        },
        onMutate: async ({ architectureId, itemId, itemType, version }) => {
            const versionsQueryKey =
                itemType === 'job-level'
                    ? jobArchitectureKeys.levelVersions(architectureId, itemId)
                    : jobArchitectureKeys.roleVersions(architectureId, itemId);

            const detailQueryKey =
                itemType === 'job-level'
                    ? jobArchitectureKeys.level(architectureId, itemId)
                    : jobArchitectureKeys.role(architectureId, itemId);

            await Promise.all([
                queryClient.cancelQueries({ queryKey: versionsQueryKey }),
                queryClient.cancelQueries({ queryKey: detailQueryKey }),
            ]);

            const currentUser = myUserProfile.isJust
                ? {
                      id: myUserProfile.value.id,
                      firstName: myUserProfile.value.firstName,
                      lastName: myUserProfile.value.lastName,
                  }
                : null;

            const publishedAt = new Date().toISOString();

            queryClient.setQueryData<JobProfileVersions>(versionsQueryKey, (old) => {
                if (old === null || old === undefined) {
                    return old;
                }

                return old.map((versionItem) => {
                    if (versionItem.version === Number(version)) {
                        return {
                            ...versionItem,
                            isPublished: true,
                            publishedAt,
                            publishedBy: currentUser,
                        };
                    }

                    return versionItem;
                });
            });

            if (itemType === 'job-role') {
                const roleDetailsQueries = queryClient.getQueriesData({
                    queryKey: jobArchitectureKeys.role(architectureId, itemId),
                });

                roleDetailsQueries.forEach(([queryKey, data]: [QueryKey, JobRoleDetails]) => {
                    if (!is.undefined(data) && !is.undefined(data.versionInfo)) {
                        if (data.versionInfo.version === Number(version)) {
                            queryClient.setQueryData(queryKey, {
                                ...data,
                                versionInfo: {
                                    ...data.versionInfo,
                                    isPublished: true,
                                    isReviewed: true,
                                },
                            });
                        }
                    }
                });

                const rolesListQueries = queryClient.getQueriesData({
                    queryKey: jobArchitectureKeys.roles(architectureId),
                });

                rolesListQueries.forEach(([queryKey, data]: [QueryKey, readonly JobRole[]]) => {
                    if (!is.undefined(data) && is.array(data)) {
                        const updatedRoles = data.map((role) => {
                            if (role.id === itemId && !is.undefined(role.versionInfo)) {
                                if (role.versionInfo.version === Number(version)) {
                                    return {
                                        ...role,
                                        versionInfo: {
                                            ...role.versionInfo,
                                            isPublished: true,
                                            isReviewed: true,
                                        },
                                    };
                                }
                            }

                            return role;
                        });

                        queryClient.setQueryData(queryKey, updatedRoles);
                    }
                });
            } else {
                const levelDetailsQueries = queryClient.getQueriesData({
                    queryKey: jobArchitectureKeys.level(architectureId, itemId),
                });

                levelDetailsQueries.forEach(([queryKey, data]: [QueryKey, JobLevelDetails]) => {
                    if (!is.undefined(data) && !is.undefined(data.versionInfo)) {
                        if (data.versionInfo.version === Number(version)) {
                            queryClient.setQueryData(queryKey, {
                                ...data,
                                versionInfo: {
                                    ...data.versionInfo,
                                    isPublished: true,
                                },
                            });
                        }
                    }
                });

                const levelsListQueries = queryClient.getQueriesData({
                    queryKey: jobArchitectureKeys.levels(architectureId),
                });

                levelsListQueries.forEach(([queryKey, data]: [QueryKey, readonly JobLevel[]]) => {
                    if (!is.undefined(data) && is.array(data)) {
                        const updatedLevels = data.map((level) => {
                            if (level.id === itemId && !is.undefined(level.versionInfo)) {
                                if (level.versionInfo.version === Number(version)) {
                                    return {
                                        ...level,
                                        versionInfo: {
                                            ...level.versionInfo,
                                            isPublished: true,
                                        },
                                    };
                                }
                            }

                            return level;
                        });

                        queryClient.setQueryData(queryKey, updatedLevels);
                    }
                });
            }
        },
        onSuccess: (_, { architectureId }) => {
            queryClient.invalidateQueries({
                queryKey: jobArchitectureKeys.architecture(architectureId),
            });
            queryClient.invalidateQueries({
                queryKey: jobArchitectureKeys.architectureDetail(architectureId),
            });
        },
    });
};
